import type { ConfiguredService } from '../types/service.types';
import type { ServiceItem } from '../types/services.types';
import type { SmriqGeneratedService, SmriqWorkItem } from '../types/smriq.types';

/** A locally-detected plan defect (duplicate id) — never sent to the quote. */
export class SmriqServiceMappingError extends Error {}

// The vendor work item in the ConfiguredService.items shape a local plan uses, so it persists
// with the quote and restores on reload. `description` is null on the wire, so fall back to name.
const toConfiguredItem = (visitId: string, item: SmriqWorkItem): ServiceItem => ({
    id: item.id,
    description: item.description ?? item.name,
    pivot: {
        service_id: visitId,
        item_id: item.id,
        unit_quantity: item.pivot.unit_quantity,
        unit_price_net: item.pivot.unit_price_net,
    },
});

// A duplicate visit id fails the whole map rather than being suffixed: the
// instanceId must keep echoing the provider's id unchanged.
export function mapSmriqServices(services: SmriqGeneratedService[]): ConfiguredService[] {
    const seenIds = new Set<string>();

    return services.map((service, index) => {
        if (seenIds.has(service.id)) {
            throw new SmriqServiceMappingError(
                'This plan contains a repeated scheduled visit. Contact support before saving this quote.',
            );
        }
        seenIds.add(service.id);

        return {
            instanceId: service.id,
            name: service.name,
            price: service.price,
            gross_price: service.gross_price,
            startDate: service.due_date,
            interval: service.interval_months ?? undefined,
            position: index + 1,
            serviceType: service.service_type,
            costMode: 'total',
            // Only the included items are the plan; declined recommendations are excluded from it.
            items: service.items.map((item) => toConfiguredItem(service.id, item)),
            // The full vendor picture (included + declined) persists too, so a resumed quote
            // rebuilds the decline UI without a fresh generate. See SmriqPersistedInclusions.
            smriqInclusions: {
                items: service.items,
                declinedItems: service.declined_items,
            },
        };
    });
}
